// Taşeron portalı — sunum yardımcıları. Sunucu UTC ISO 8601 / DateOnly gönderir
// (api-contract §4); yerelleştirme sunum katmanındadır. Geçersiz/boş değerde tire döner.

/** UTC ISO / DateOnly tarihini aktif kültüre göre kısa tarih olarak biçimlendirir. */
export function formatDate(iso: string | null | undefined, culture: string): string {
  if (!iso) {
    return '—';
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  try {
    return new Intl.DateTimeFormat(culture, { dateStyle: 'medium' }).format(date);
  } catch {
    return date.toISOString().slice(0, 10);
  }
}

/**
 * UTC ISO tarih-saat değerini aktif kültüre göre kısa tarih+saat olarak biçimlendirir.
 * Davet son geçerlilik anı gibi saat-ölçekli (TTL) değerler içindir (yalnız tarih yanıltır).
 */
export function formatDateTime(iso: string | null | undefined, culture: string): string {
  if (!iso) {
    return '—';
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  try {
    return new Intl.DateTimeFormat(culture, { dateStyle: 'medium', timeStyle: 'short' }).format(
      date,
    );
  } catch {
    return date.toISOString();
  }
}

/**
 * Bayt değerini kültüre göre kısa boyut dizesine çevirir (Belge Analizi §20/S17 —
 * yükleme kuyruğu + kapasite göstergesi). Birim kısaltmaları (B/KB/MB/GB) üç dilde de
 * AYNIDIR; yalnız ondalık AYIRICI kültüre göre değişir (tr/de virgül, en nokta —
 * `Intl.NumberFormat` bunu üstlenir, elle dize biçimlendirme YOK). Negatif/`NaN` → tire.
 */
export function formatBytes(bytes: number, culture: string): string {
  if (!Number.isFinite(bytes) || bytes < 0) {
    return '—';
  }
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  const units = ['KB', 'MB', 'GB'] as const;
  let value = bytes / 1024;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  // `noUncheckedIndexedAccess`: değişken indeksle tuple erişimi `| undefined` döner —
  // döngü sınırı bunu çalışma zamanında zaten garanti eder, geri düşüş yalnız tip içindir.
  const unit = units[unitIndex] ?? 'GB';
  let formatted: string;
  try {
    formatted = new Intl.NumberFormat(culture, {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }).format(value);
  } catch {
    formatted = value.toFixed(1);
  }
  return `${formatted} ${unit}`;
}

/**
 * Belge Analizi gelen kutusu — sol raydaki 1. seviye satırın kısa tarih-saat biçimi
 * ("10 Ağu 09:26"). Kasıtlı olarak DOĞAL harf büyüklüğünde döner — eyebrow stili (`uppercase`
 * class'ı) görsel büyütmeyi CSS'te üstlenir; dizeye BÜYÜK HARF gömülmez (bkz. `i18n.ts`
 * `syncDocumentLang` — Almanca metin `tr` kasasıyla büyütülürse `WİCHTİG` gibi yanlış nokta'lı
 * İ üretir; kaynak dizeyi doğal bırakmak bu riski taşımaz, `<html lang>` zaten senkron).
 */
export function formatShortDateTime(iso: string, culture: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  try {
    return new Intl.DateTimeFormat(culture, {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  } catch {
    return date.toISOString();
  }
}
